import React, { useCallback, useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Info, TriangleAlert, RefreshCw, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import ErrorsSparkline from "@/components/admin/ErrorsSparkline";

const LEVEL_ICON = {
  error: <AlertTriangle size={14} className="text-red-600" />,
  warning: <TriangleAlert size={14} className="text-amber-600" />,
  info: <Info size={14} className="text-primary" />,
};

const SOURCE_LABEL = {
  window: "window.onerror",
  promise: "unhandled promise",
  react: "react",
  manual: "manual",
};

export default function AdminErrors() {
  const [events, setEvents] = useState([]);
  const [counts, setCounts] = useState({ error: 0, warning: 0, info: 0, total: 0 });
  const [series, setSeries] = useState([]);
  const [level, setLevel] = useState("all");
  const [source, setSource] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (level !== "all") params.level = level;
      if (source !== "all") params.source = source;
      const [listRes, seriesRes] = await Promise.all([
        api.get("/admin/errors", { params }),
        api.get("/admin/errors/daily", { params: { days: 30 } }),
      ]);
      setEvents(listRes.data.events || []);
      setCounts(listRes.data.counts || { error: 0, warning: 0, info: 0, total: 0 });
      setSeries(seriesRes.data.series || []);
    } catch (err) {
      console.error("Failed to load errors:", err);
      toast.error("Could not load error events");
    } finally {
      setLoading(false);
    }
  }, [level, source]);

  useEffect(() => { load(); }, [load]);

  const clearAll = async () => {
    if (!window.confirm("Delete ALL stored error events? This cannot be undone.")) return;
    try {
      await api.delete("/admin/errors");
      toast.success("Cleared error events");
      load();
    } catch (err) {
      console.error("Failed to clear errors:", err);
      toast.error("Could not clear events");
    }
  };

  return (
    <AppShell
      pageTitle="Diagnostics · Error events"
      pageDescription="First-party error logger — no third parties. Only consented sessions are recorded. Server redacts common PII from message and stack."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="errors-refresh">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
          <Button variant="destructive" onClick={clearAll} data-testid="errors-clear">
            <Trash2 size={14} className="mr-1" /> Clear
          </Button>
        </div>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: "total", label: "Total captured" },
          { key: "error", label: "Errors" },
          { key: "warning", label: "Warnings" },
          { key: "info", label: "Info" },
        ].map((k) => (
          <div key={k.key} className="rounded-lg border border-border bg-white p-4" data-testid={`errors-kpi-${k.key}`}>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
            <div className="mt-2 font-serif text-2xl text-primary">{counts[k.key] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-border bg-white p-4" data-testid="errors-sparkline-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Last 30 days</div>
            <div className="mt-1 font-serif text-primary text-lg">Daily error volume</div>
          </div>
          <div className="text-[11px] text-muted-foreground">
            {series.length ? `${series.reduce((a, d) => a + (d.total || 0), 0)} total events` : ""}
          </div>
        </div>
        <div className="mt-3">
          <ErrorsSparkline series={series} height={80} />
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Level</span>
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="h-9 w-40" data-testid="errors-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All levels</SelectItem>
              <SelectItem value="error">Errors</SelectItem>
              <SelectItem value="warning">Warnings</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Source</span>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="h-9 w-48" data-testid="errors-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              <SelectItem value="window">window.onerror</SelectItem>
              <SelectItem value="promise">unhandled promise</SelectItem>
              <SelectItem value="react">react</SelectItem>
              <SelectItem value="manual">manual</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-serif text-primary text-lg">No error events</div>
            <p className="mt-1 text-sm text-muted-foreground">Users have not opted in yet, or nothing has broken. Both are good outcomes.</p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {events.map((e) => (
              <li key={e.id} className="p-4 hover:bg-muted/30 transition-colors" data-testid={`error-row-${e.id}`}>
                <button
                  onClick={() => setExpanded((prev) => ({ ...prev, [e.id]: !prev[e.id] }))}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 shrink-0">{LEVEL_ICON[e.level] || LEVEL_ICON.error}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-primary truncate">{e.message}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                        <span>{new Date(e.created_at).toLocaleString()}</span>
                        <span>· {SOURCE_LABEL[e.source] || e.source}</span>
                        {e.route_path ? <span>· {e.route_path}</span> : null}
                        {e.ip_prefix ? <span>· {e.ip_prefix}</span> : null}
                      </div>
                    </div>
                  </div>
                </button>
                {expanded[e.id] && e.stack ? (
                  <pre className="mt-3 max-h-64 overflow-auto rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">{e.stack}</pre>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
